import { useState, useEffect } from 'react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { ImageUploadSection } from './ImageUploadSection';
import { getAllArchitectureDrawings, type ProcessedArchitectureDrawing } from '@/lib/architecture-drawings';
import type { Tables } from '@/integrations/supabase/types';
import { RotateCcw } from 'lucide-react';

type PropertyUnit = Tables<'property_units'>;

interface UnitFormData {
  availability: 'available' | 'reserved' | 'sold';
  view_type: 'lagoon' | 'canal';
  price: string;
  show_price: boolean;
  is_published: boolean;
  matterport_url: string;
  kuula_url: string;
  images: string[];
  floor_plan_images: string[];
  render_images: string[];
  render_rotation_angle: number;
  architecture_drawing_id: string | null;
}

interface UnitEditFormProps {
  unit: PropertyUnit | null;
  formData: UnitFormData;
  onUpdate: (field: keyof UnitFormData, value: UnitFormData[keyof UnitFormData]) => void;
  architectureDrawing?: ProcessedArchitectureDrawing | null;
}

export function UnitEditForm({ unit, formData, onUpdate, architectureDrawing }: UnitEditFormProps) {
  const [drawingsList, setDrawingsList] = useState<ProcessedArchitectureDrawing[]>([]);

  useEffect(() => {
    getAllArchitectureDrawings().then(setDrawingsList);
  }, []);

  if (!unit) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-muted-foreground">Loading unit data...</p>
      </div>
    );
  }

  // Resolve displayed linked drawing: manual link from list, or unit-type default
  const linkedDrawing =
    formData.architecture_drawing_id
      ? drawingsList.find((d) => d.id === formData.architecture_drawing_id)
      : architectureDrawing ?? null;
  const linkedFloorPlans = linkedDrawing
    ? [{
        label: linkedDrawing.displayName || `Architecture Drawing (${linkedDrawing.unitType})`,
        thumbnailUrl: linkedDrawing.thumbnailUrl,
        imageUrls: linkedDrawing.imageUrls,
        isLinked: true as const,
      }]
    : [];

  return (
    <div className="space-y-6">
      {/* Basic Information */}
      <Card>
        <CardHeader>
          <CardTitle>Basic Information</CardTitle>
          <CardDescription>Unit availability and pricing details</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Availability */}
          <div className="space-y-2">
            <Label htmlFor="availability">Availability Status</Label>
            <Select
              value={formData.availability}
              onValueChange={(value) => onUpdate('availability', value as 'available' | 'reserved' | 'sold')}
            >
              <SelectTrigger id="availability">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="available">Available</SelectItem>
                <SelectItem value="reserved">Reserved</SelectItem>
                <SelectItem value="sold">Sold</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* View Type */}
          <div className="space-y-2">
            <Label htmlFor="view_type">View Type</Label>
            <Select
              value={formData.view_type}
              onValueChange={(value) => onUpdate('view_type', value)}
            >
              <SelectTrigger id="view_type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="lagoon">Lagoon View</SelectItem>
                <SelectItem value="canal">Canal View</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Price */}
          <div className="space-y-2">
            <Label htmlFor="price">Price (USD)</Label>
            <Input
              id="price"
              type="number"
              placeholder="Enter price"
              value={formData.price}
              onChange={(e) => onUpdate('price', e.target.value)}
            />
          </div>

          {/* Show Price Toggle */}
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="show_price">Display Price</Label>
              <p className="text-sm text-muted-foreground">
                Show price on public listing
              </p>
            </div>
            <Switch
              id="show_price"
              checked={formData.show_price}
              onCheckedChange={(checked) => onUpdate('show_price', checked)}
            />
          </div>
        </CardContent>
      </Card>

      {/* 3D Virtual Tours */}
      <Card>
        <CardHeader>
          <CardTitle>3D Virtual Tours</CardTitle>
          <CardDescription>Add virtual tour links for immersive viewing</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="matterport_url">Matterport URL</Label>
            <Input
              id="matterport_url"
              type="url"
              placeholder="https://..."
              value={formData.matterport_url}
              onChange={(e) => onUpdate('matterport_url', e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="kuula_url">Kuula 360° URL</Label>
            <Input
              id="kuula_url"
              type="url"
              placeholder="https://..."
              value={formData.kuula_url}
              onChange={(e) => onUpdate('kuula_url', e.target.value)}
            />
          </div>
        </CardContent>
      </Card>

      {/* Gallery Images */}
      <Card>
        <CardHeader>
          <CardTitle>Gallery Images</CardTitle>
          <CardDescription>Upload up to 30 property images</CardDescription>
        </CardHeader>
        <CardContent>
          <ImageUploadSection
            unitId={unit.id}
            imageType="gallery"
            existingImages={formData.images}
            onImagesUpdated={(images) => onUpdate('images', images)}
            maxImages={30}
          />
        </CardContent>
      </Card>

      {/* Floor Plans */}
      <Card>
        <CardHeader>
          <CardTitle>Floor Plans</CardTitle>
          <CardDescription>Link to an architecture drawing or upload per-unit floor plan images</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="architecture_drawing">Link to architecture drawing</Label>
            <Select
              value={formData.architecture_drawing_id ?? '__default__'}
              onValueChange={(value) => onUpdate('architecture_drawing_id', value === '__default__' ? null : value)}
            >
              <SelectTrigger id="architecture_drawing">
                <SelectValue placeholder="Use unit type default" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__default__">Use unit type default</SelectItem>
                {drawingsList.map((d) => (
                  <SelectItem key={d.id} value={d.id}>
                    {d.displayName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Optional. When set, this drawing is shown for the unit instead of the one for its unit type.
            </p>
          </div>
          <ImageUploadSection
            unitId={unit.id}
            imageType="floor_plan"
            existingImages={formData.floor_plan_images}
            onImagesUpdated={(images) => onUpdate('floor_plan_images', images)}
            maxImages={5}
            linkedImages={linkedFloorPlans}
          />
        </CardContent>
      </Card>

      {/* 3D Renders */}
      <Card>
        <CardHeader>
          <CardTitle>3D Renders</CardTitle>
          <CardDescription>Upload up to 10 3D interior render images</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <ImageUploadSection
            unitId={unit.id}
            imageType="render"
            existingImages={formData.render_images}
            onImagesUpdated={(images) => onUpdate('render_images', images)}
            maxImages={10}
            label="3D Renders"
          />

          {/* Rotation Control */}
          {formData.render_images.length > 0 && (
            <div className="space-y-4 pt-4 border-t">
              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="render_rotation">Rotation Angle</Label>
                  <p className="text-sm text-muted-foreground">
                    Adjust to match architectural floor plan orientation
                  </p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onUpdate('render_rotation_angle', 0)}
                  disabled={formData.render_rotation_angle === 0}
                >
                  <RotateCcw className="h-4 w-4 mr-2" />
                  Reset
                </Button>
              </div>
              
              <div className="space-y-3">
                <Slider
                  id="render_rotation"
                  min={0}
                  max={360}
                  step={1}
                  value={[formData.render_rotation_angle]}
                  onValueChange={(value) => onUpdate('render_rotation_angle', value[0])}
                  className="w-full"
                />
                
                <div className="flex items-center gap-4">
                  <Input
                    type="number"
                    min={0}
                    max={360}
                    step={0.5}
                    value={formData.render_rotation_angle}
                    onChange={(e) => {
                      const val = parseFloat(e.target.value);
                      if (!isNaN(val) && val >= 0 && val <= 360) {
                        onUpdate('render_rotation_angle', val);
                      }
                    }}
                    className="w-24"
                  />
                  <span className="text-sm text-muted-foreground">degrees</span>
                  
                  {/* Quick rotation buttons */}
                  <div className="flex gap-1 ml-auto">
                    {[0, 90, 180, 270].map((angle) => (
                      <Button
                        key={angle}
                        variant={formData.render_rotation_angle === angle ? "default" : "outline"}
                        size="sm"
                        onClick={() => onUpdate('render_rotation_angle', angle)}
                      >
                        {angle}°
                      </Button>
                    ))}
                  </div>
                </div>

                {/* Preview thumbnail with rotation */}
                {formData.render_images[0] && (
                  <div className="mt-4 p-4 bg-muted rounded-lg">
                    <p className="text-xs text-muted-foreground mb-2">Preview (first render with rotation applied)</p>
                    <div className="flex justify-center overflow-hidden rounded-lg bg-background">
                      <img
                        src={formData.render_images[0]}
                        alt="Rotation preview"
                        className="max-h-48 object-contain transition-transform duration-300"
                        style={{ transform: `rotate(${formData.render_rotation_angle}deg)` }}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
